import React, { createContext, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase/client'

interface AdminSettings {
  gtmId: string
  headerScripts: string
  bodyScripts: string
}

interface AdminContextType extends AdminSettings {
  updateSettings: (settings: AdminSettings) => Promise<void>
  loading: boolean
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<AdminSettings>({
    gtmId: '',
    headerScripts: '',
    bodyScripts: '',
  })
  const [loading, setLoading] = useState(true)

  // Fetch settings from Supabase on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('*')
          .single()

        if (error) {
          console.error('Error fetching settings:', error)
          // Don't throw here, just let it use defaults
          return
        }

        if (data) {
          setSettings({
            gtmId: data.gtm_id || '',
            headerScripts: data.header_scripts || '',
            bodyScripts: data.body_scripts || '',
          })
        }
      } catch (error) {
        console.error('Unexpected error fetching settings:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [])

  const updateSettings = async (newSettings: AdminSettings) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({
          gtm_id: newSettings.gtmId,
          header_scripts: newSettings.headerScripts,
          body_scripts: newSettings.bodyScripts,
          updated_at: new Date().toISOString(),
        })
        .eq('id', 1)

      if (error) throw error

      setSettings(newSettings)
      toast.success('Configurações salvas com sucesso!')
    } catch (error: any) {
      console.error('Error updating settings:', error)
      toast.error('Erro ao salvar configurações.')
      throw error // Re-throw to let the component know
    }
  }

  return (
    <AdminContext.Provider
      value={{
        ...settings,
        updateSettings,
        loading,
      }}
    >
      {children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (!context) {
    throw new Error('useAdmin must be used within a AdminProvider')
  }
  return context
}
